import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { CatalogResourceDc, CatalogResourceType, ResourceTypeLink } from "@evergis/api";
import { ThemeName } from "@evergis/ui";

import { DIALOGS } from "components/Dialogs/constants";
import { setCurrentDialog } from "ducks/dialogs/dialogs";
import { useProjectInfo } from "ducks/project/hooks";
import { useResetFeatureCard } from "modules/FeatureCard/hooks/useResetFeatureCard";
import { setPrintEnabled } from "../../../ducks/print/print";
import { createTemporaryProject } from "../../../ducks/project/project";
import { changeThemeName } from "../../../ducks/theme/theme";
import { useOpenProject } from "../../../hooks/project/useOpenProject";
import { useEditResourceDialog, useSelectFilesDialog } from "../../../hooks/useDialogs";
import { useResourceType } from "../../../hooks/useResourceType";
import { useLimitsExceededCheck } from "../../../hooks/useLimitsExceededCheck";
import { goToProjectConfiguration } from "../../../ducks/location/location";

// eslint-disable-next-line max-lines-per-function
export const usePanelSelectHandlers = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [{ name, alias }] = useProjectInfo();
  const openEditResourceDialog = useEditResourceDialog();
  const [resourceType] = useResourceType();
  const openEditDialog = useCallback(() => openEditResourceDialog([name, resourceType]), [name, resourceType]);
  const openSaveProjectDialog = useLimitsExceededCheck(useEditResourceDialog(), true);
  const openSelectFiles = useSelectFilesDialog();
  const openProject = useOpenProject();

  const saveAsNew = useResetFeatureCard(() => {
    dispatch(
      setCurrentDialog(DIALOGS.COPY_CURRENT_PROJECT, {
        resources: [name, ResourceTypeLink.Project],
      }),
    );
  }, [dispatch, name]);

  const openAnother = useResetFeatureCard(
    () =>
      openSelectFiles([""], {
        onApply: (selectedResources: CatalogResourceDc[]) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          openProject(selectedResources[0].systemName);
        },
        filters: {
          resourceTypes: [CatalogResourceType.Map],
        },
        hasActions: false,
        actionText: t("actions.open"),
      }),
    [openSelectFiles, t],
  );

  const print = useResetFeatureCard(() => dispatch(setPrintEnabled(true)), [dispatch]);

  const deleteProject = useResetFeatureCard(
    () => dispatch(setCurrentDialog(DIALOGS.DELETE_RESOURCES, { resources: [name] })),
    [dispatch, name],
  );

  const share = useCallback(() => {
    dispatch(setCurrentDialog(DIALOGS.SHARE_DIALOG, { resources: [name] }));
  }, [dispatch, name]);

  const copyWms = useResetFeatureCard(
    () => dispatch(setCurrentDialog(DIALOGS.WMS_LINK, { resources: [name, ResourceTypeLink.Project, alias] })),
    [dispatch, name, alias],
  );

  const newMap = useCallback(() => {
    dispatch(createTemporaryProject());
  }, [dispatch]);

  const lightTheme = useCallback(() => {
    dispatch(changeThemeName(ThemeName.Light));
  }, [dispatch]);

  const darkTheme = useCallback(() => {
    dispatch(changeThemeName(ThemeName.Dark));
  }, [dispatch]);

  const saveTemporaryProject = useCallback(() => {
    openSaveProjectDialog([name, ResourceTypeLink.Project], { saveMode: true });
  }, [openSaveProjectDialog, name]);

  const configurations = useCallback(() => {
    dispatch(goToProjectConfiguration());
  }, [dispatch]);

  return {
    newMap,
    "save-as-new": saveAsNew,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    "favorite-change": () => () => {},
    "open-another": openAnother,
    "copy-wms": copyWms,
    "save-temp-project": saveTemporaryProject,
    print,
    projectProperties: openEditDialog,
    delete: deleteProject,
    share,
    lightTheme,
    darkTheme,
    configurations,
  } as const;
};
